import { useEffect, useRef, useState } from 'react';
import ApiBaseUrl from 'src/Common/ApiBaseURL';
import i18n from 'src/i18n/config';
import { getWorkerVersion } from 'src/serviceWorkerRegistration';
import {
  AUTH_TOKEN_CHANGED_EVENT,
  describeWebPlatform,
  getPersistedWebDeviceId,
  getStoreDeviceSession,
  getWebDeviceName
} from './deviceIdentity';

// =====================================================================================
// Centrally managed device settings — the web browser (appType 'web').
//
// The Developer Settings module can set this browser's localStorage preferences at company,
// branch or device level. The server resolves them (device > branch > company) and this module:
//   1. registers the browser once per store session and pulls the resolved values on login,
//      boot, every SettingsChanged("device_defaults" | "*") push and every hub (re)connect;
//   2. writes each managed value into localStorage in the screen's own raw format, after saving
//      the user's own value in the `prior` ledger, and gives that value back when the key stops
//      being managed (or a different company signs in on this browser);
//   3. re-writes managed values that went missing or were changed locally (verify pass);
//   4. reports the local values (debounced, de-duplicated across tabs);
//   5. tells the open screens through the window event 'deviceSettingsApplied' { keys } so they
//      re-read their value, and locks every control bound to a managed key.
//
// Only an authoritative answer changes anything: HTTP 200, managed === true, the session's own
// company and device, and no login/logout while it was in flight. 404 (older server), managed:false
// (tables not migrated / not a store session), 401, 429, 5xx, timeouts and malformed bodies all mean
// "no change". These calls use a plain fetch: a 401 here never signs the user out.
//
// Every tab has its own JS context but they share localStorage, so the ledger decisions run inside
// navigator.locks 'cloudpos-device-settings' and re-read the ledger inside the lock.
// =====================================================================================

export type DeviceSettingLevel = 'company' | 'branch' | 'device';

export const WEB_APP_TYPE = 'web';
/** SettingsChanged area that carries device-setting changes. */
export const DEVICE_DEFAULTS_AREA = 'device_defaults';
/** Window event fired after an apply / restore / verify pass, detail `{ keys }` (`['*']` = all). */
export const DEVICE_SETTINGS_APPLIED_EVENT = 'deviceSettingsApplied';

export interface DeviceSettingsAppliedDetail {
  keys: string[];
}

/** Registry keys of the web app (the ones a screen binds to). */
export const WebDeviceSetting = {
  themeMode: 'device.web.theme_mode',
  language: 'device.web.language',
  kdsSoundEnabled: 'device.web.kds.sound_enabled',
  kdsSoundOnNew: 'device.web.kds.sound_on_new',
  kdsSoundOnUpdate: 'device.web.kds.sound_on_update',
  posScanSound: 'device.web.pos.scan_sound',
  terminal3ItemView: 'device.web.terminal3.item_view',
  purchaseLayout: 'device.web.purchase.layout',
  transferItemView: 'device.web.transfer.item_view',
  itemsFiltersOpen: 'device.web.items.filters_open',
  stockAuditScanSound: 'device.web.stock_audit.scan_sound',
  stockAuditDeviceName: 'device.web.stock_audit.device_name',
  bookingsViewMode: 'device.web.bookings.view_mode',
  setupLayoutView: 'device.web.setup.layout_view',
  reportsLayoutView: 'device.web.reports.layout_view',
  productionOrderPaper: 'device.web.production_order.paper',
  developerLayoutView: 'device.web.developer.layout_view'
} as const;

const LEDGER_KEY = 'cloudpos_device_ledger';
const REPORT_HASH_KEY = 'cloudpos_device_last_report_hash';
const LOCK_NAME = 'cloudpos-device-settings';

const REQUEST_TIMEOUT_MS = 15000;
const REPORT_DEBOUNCE_MS = 10000;
/** The server allows one report per device per 10 s; used when a 429 carries no readable delay. */
const REPORT_RETRY_FALLBACK_MS = 10000;
/** Re-send an identical snapshot after a day anyway (the server row may have been reset). */
const REPORT_HASH_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_REPORT_VALUE_BYTES = 16 * 1024;
const MAX_REPORT_BODY_BYTES = 900 * 1024;

// ── The key table ─────────────────────────────────────────────────────────────────────────
// storageKey + raw format exactly as the screens read and write them. `effective` decodes a raw
// local value into the typed value the screen actually runs with (what the report sends).

type SettingValue = string | boolean;
type Kind = 'enum' | 'bool' | 'string';

interface WebKeyDef {
  key: string;
  storageKey: string;
  kind: Kind;
  allowed?: readonly string[];
  maxLength?: number;
  /** Reported only, never applied (Developer-only screen). */
  reportOnly?: boolean;
  effective: (raw: string | null) => SettingValue | null;
}

const LANGUAGES = ['en', 'ur', 'fr', 'ar'] as const;
const LANGUAGE_STORAGE_KEY = 'i18nextLng';

/** 'en-US' → 'en'. */
export const baseLanguageCode = (code: string | null | undefined): string | null => {
  if (!code) return null;
  const base = code.trim().toLowerCase().split(/[-_]/)[0];
  return base || null;
};

const oneOf = (allowed: readonly string[], fallback: string) => (raw: string | null) =>
  raw !== null && allowed.includes(raw) ? raw : fallback;
const onOff = (raw: string | null) => (raw === 'off' ? 'off' : 'on');
const boolOr = (fallback: boolean | null) => (raw: string | null) =>
  raw === 'true' ? true : raw === 'false' ? false : fallback;

const LAYOUTS = ['detailed', 'compact'] as const;

const WEB_DEVICE_KEYS: readonly WebKeyDef[] = [
  {
    key: WebDeviceSetting.themeMode,
    storageKey: 'appTheme',
    kind: 'enum',
    allowed: ['PureLightTheme', 'DarkTheme'],
    effective: (raw) => raw || 'PureLightTheme'
  },
  {
    key: WebDeviceSetting.language,
    storageKey: LANGUAGE_STORAGE_KEY,
    kind: 'enum',
    allowed: LANGUAGES,
    effective: (raw) => {
      const code = baseLanguageCode(raw) ?? baseLanguageCode(i18n.language);
      return code && (LANGUAGES as readonly string[]).includes(code) ? code : 'en';
    }
  },
  { key: WebDeviceSetting.kdsSoundEnabled, storageKey: 'kitchenDisplay_soundEnabled', kind: 'bool', effective: boolOr(true) },
  { key: WebDeviceSetting.kdsSoundOnNew, storageKey: 'kitchenDisplay_playSoundOnNew', kind: 'bool', effective: boolOr(true) },
  { key: WebDeviceSetting.kdsSoundOnUpdate, storageKey: 'kitchenDisplay_playSoundOnUpdate', kind: 'bool', effective: boolOr(false) },
  { key: WebDeviceSetting.posScanSound, storageKey: 'cloudpos_pos_scan_sound', kind: 'enum', allowed: ['on', 'off'], effective: onOff },
  {
    key: WebDeviceSetting.terminal3ItemView,
    storageKey: 'touchPosItemView',
    kind: 'enum',
    allowed: ['card', 'list'],
    effective: oneOf(['card', 'list'], 'card')
  },
  {
    key: WebDeviceSetting.purchaseLayout,
    storageKey: 'purchaseLayout',
    kind: 'enum',
    allowed: ['grid', 'browse'],
    effective: (raw) => (raw === 'grid' ? 'grid' : 'browse')
  },
  {
    key: WebDeviceSetting.transferItemView,
    storageKey: 'transferItemView',
    kind: 'enum',
    allowed: ['card', 'list'],
    effective: oneOf(['card', 'list'], 'card')
  },
  // Missing = open on large screens, collapsed below: no single value to report.
  { key: WebDeviceSetting.itemsFiltersOpen, storageKey: 'items_filters_open', kind: 'bool', effective: boolOr(null) },
  {
    key: WebDeviceSetting.stockAuditScanSound,
    storageKey: 'cloudpos_stock_audit_sound',
    kind: 'enum',
    allowed: ['on', 'off'],
    effective: onOff
  },
  {
    key: WebDeviceSetting.stockAuditDeviceName,
    storageKey: 'cloudpos_stock_audit_device_name',
    kind: 'string',
    maxLength: 150,
    effective: (raw) => (raw && raw.trim() ? raw : 'CloudPOS Web')
  },
  {
    key: WebDeviceSetting.bookingsViewMode,
    storageKey: 'bookingsViewMode',
    kind: 'enum',
    allowed: ['calendar', 'grid', 'month'],
    effective: oneOf(['calendar', 'grid', 'month'], 'calendar')
  },
  {
    key: WebDeviceSetting.setupLayoutView,
    storageKey: 'cloudpos_setup_layout_view',
    kind: 'enum',
    allowed: LAYOUTS,
    effective: oneOf(LAYOUTS, 'detailed')
  },
  {
    key: WebDeviceSetting.reportsLayoutView,
    storageKey: 'reportsPage.layoutView',
    kind: 'enum',
    allowed: LAYOUTS,
    effective: oneOf(LAYOUTS, 'detailed')
  },
  {
    key: WebDeviceSetting.productionOrderPaper,
    storageKey: 'cloudpos_production_order_paper',
    kind: 'enum',
    allowed: ['auto', 'A4', 'Thermal80mm', 'Thermal58mm'],
    effective: oneOf(['auto', 'A4', 'Thermal80mm', 'Thermal58mm'], 'auto')
  },
  {
    key: WebDeviceSetting.developerLayoutView,
    storageKey: 'cloudpos_developer_layout_view',
    kind: 'enum',
    allowed: LAYOUTS,
    reportOnly: true,
    effective: oneOf(LAYOUTS, 'detailed')
  }
];
// device.web.terminal3.items_pane_width is dead (never rendered): neither applied nor reported.

const KEYS_BY_ID: Record<string, WebKeyDef> = WEB_DEVICE_KEYS.reduce((acc, def) => {
  acc[def.key] = def;
  return acc;
}, {} as Record<string, WebKeyDef>);

/** The typed value this browser currently runs with for `key` (null = nothing decidable). */
export function readWebDeviceSetting(key: string): SettingValue | null {
  const def = KEYS_BY_ID[key];
  return def ? def.effective(readRaw(def.storageKey)) : null;
}

const encode = (def: WebKeyDef, value: SettingValue): string =>
  def.kind === 'bool' ? (value ? 'true' : 'false') : String(value);

/** The managed value when it is valid for the key (Type + AllowedValues + length), else undefined. */
function validManagedValue(def: WebKeyDef, value: unknown): SettingValue | undefined {
  if (def.kind === 'bool') return typeof value === 'boolean' ? value : undefined;
  if (typeof value !== 'string') return undefined;
  if (def.kind === 'enum') return def.allowed && def.allowed.includes(value) ? value : undefined;
  if (value.trim().length === 0 || value.length > (def.maxLength ?? 200)) return undefined;
  return value;
}

const LEVELS: readonly DeviceSettingLevel[] = ['company', 'branch', 'device'];
const normalizeLevel = (v: unknown): DeviceSettingLevel => {
  const s = String(v ?? '').toLowerCase();
  return (LEVELS as readonly string[]).includes(s) ? (s as DeviceSettingLevel) : 'company';
};

// ── localStorage helpers (never throw) ────────────────────────────────────────────────────

function readRaw(storageKey: string): string | null {
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

function writeRaw(storageKey: string, raw: string): boolean {
  try {
    localStorage.setItem(storageKey, raw);
    return true;
  } catch {
    return false;
  }
}

function removeRaw(storageKey: string): void {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    /* ignore */
  }
}

// ── The ledger ────────────────────────────────────────────────────────────────────────────
// localStorage, next to the values it protects (same wipe domain): clearing site data drops both.

interface LedgerEntry {
  value: SettingValue;
  level: DeviceSettingLevel;
}

interface PriorEntry {
  present: boolean;
  raw: string | null;
}

interface DeviceLedger {
  companyId: number;
  deviceId: string;
  revision: string | null;
  managed: Record<string, LedgerEntry>;
  prior: Record<string, PriorEntry>;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

function readLedger(): DeviceLedger | null {
  try {
    const raw = localStorage.getItem(LEDGER_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!isPlainObject(p)) return null;
    const companyId = Number(p.companyId);
    if (!Number.isSafeInteger(companyId) || companyId <= 0) return null;
    const managed: Record<string, LedgerEntry> = {};
    if (isPlainObject(p.managed)) {
      Object.entries(p.managed).forEach(([k, e]) => {
        if (isPlainObject(e) && (typeof e.value === 'string' || typeof e.value === 'boolean')) {
          managed[k] = { value: e.value, level: normalizeLevel(e.level) };
        }
      });
    }
    const prior: Record<string, PriorEntry> = {};
    if (isPlainObject(p.prior)) {
      Object.entries(p.prior).forEach(([k, e]) => {
        if (!isPlainObject(e)) return;
        prior[k] = e.present === true && typeof e.raw === 'string' ? { present: true, raw: e.raw } : { present: false, raw: null };
      });
    }
    return {
      companyId,
      deviceId: typeof p.deviceId === 'string' ? p.deviceId : '',
      revision: typeof p.revision === 'string' ? p.revision : null,
      managed,
      prior
    };
  } catch {
    return null;
  }
}

function writeLedger(ledger: DeviceLedger): boolean {
  return writeRaw(LEDGER_KEY, JSON.stringify(ledger));
}

/** Give the user's own value back. No prior recorded (it equalled the managed value) = keep. */
function restorePrior(def: WebKeyDef, prior: PriorEntry | undefined): boolean {
  if (!prior) return false;
  const current = readRaw(def.storageKey);
  if (prior.present && prior.raw !== null) {
    if (current === prior.raw) return false;
    return writeRaw(def.storageKey, prior.raw);
  }
  if (current === null) return false;
  removeRaw(def.storageKey);
  return true;
}

/** Re-write every managed value that went missing or was changed locally. Never touches `prior`. */
function verifyPass(ledger: DeviceLedger): string[] {
  const changed: string[] = [];
  Object.entries(ledger.managed).forEach(([key, entry]) => {
    const def = KEYS_BY_ID[key];
    if (!def || def.reportOnly) return;
    const want = encode(def, entry.value);
    if (readRaw(def.storageKey) !== want && writeRaw(def.storageKey, want)) changed.push(key);
  });
  return changed;
}

/** A different company now owns this browser's session: restore every prior, drop the ledger. */
function releaseLedger(ledger: DeviceLedger): string[] {
  const changed: string[] = [];
  Object.keys(ledger.managed).forEach((key) => {
    const def = KEYS_BY_ID[key];
    if (def && restorePrior(def, ledger.prior[key])) changed.push(key);
  });
  removeRaw(LEDGER_KEY);
  return changed;
}

async function withDeviceSettingsLock<T>(task: () => T): Promise<T> {
  let locks: LockManager | undefined;
  try {
    locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  } catch {
    locks = undefined;
  }
  // Web Locks exist only in secure contexts; a plain-HTTP origin runs unserialized (best effort).
  if (!locks || typeof locks.request !== 'function') return task();
  return locks.request(LOCK_NAME, async () => task()) as Promise<T>;
}

// ── Live apply ────────────────────────────────────────────────────────────────────────────

function dispatchApplied(keys: string[]): void {
  try {
    const detail: DeviceSettingsAppliedDetail = { keys };
    window.dispatchEvent(new CustomEvent(DEVICE_SETTINGS_APPLIED_EVENT, { detail }));
  } catch {
    /* ignore */
  }
}

const setDocumentDirection = (code: string | null) => {
  try {
    document.documentElement.setAttribute('dir', code === 'ar' || code === 'ur' ? 'rtl' : 'ltr');
  } catch {
    /* ignore */
  }
};

/** i18n follows i18nextLng now (react-i18next re-renders every screen), with the page direction. */
function applyLanguageFromStorage(): void {
  try {
    const raw = readRaw(LANGUAGE_STORAGE_KEY);
    const code = baseLanguageCode(raw);
    if (code && (LANGUAGES as readonly string[]).includes(code)) {
      // Compare base codes: a detected 'en-US' already renders 'en' and is left as the user had it.
      if (baseLanguageCode(i18n.language) !== code) void i18n.changeLanguage(code);
      setDocumentDirection(code);
    } else if (raw === null) {
      // Restored to "never chosen": detect again, exactly like a fresh page load.
      void i18n.changeLanguage().then(() => setDocumentDirection(baseLanguageCode(i18n.language)));
    }
  } catch {
    /* keep the current language */
  }
}

/** Live-apply what changed, then let the screens re-read (theme: DeviceSettingsBridge). */
function finishApply(changed: string[]): void {
  if (changed.includes(WebDeviceSetting.language) || changed.includes('*')) applyLanguageFromStorage();
  dispatchApplied(changed);
}

// Another tab applied, restored or verified values: bring this tab's screens along. The ledger is
// written before the values, so a managed value written afterwards is followed by its own key.
// Any other write of a managed key from another tab (a signed-out tab's language picker, say) is
// a local write: the verify pass puts the managed value back first.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key === LEDGER_KEY || e.key === null) {
      if (getStoreDeviceSession()) finishApply(['*']);
      else dispatchApplied([]);
      return;
    }
    const def = WEB_DEVICE_KEYS.find((d) => d.storageKey === e.key);
    if (def && getManagedDeviceSetting(def.key).locked) void verifyAfterExternalWrite(def.key);
  });
}

async function verifyAfterExternalWrite(key: string): Promise<void> {
  const s = active;
  let changed: string[] = [];
  if (s && isCurrent(s)) {
    try {
      changed = await withDeviceSettingsLock(() => (isCurrent(s) ? verifyOnly(s) : []));
    } catch {
      changed = [];
    }
  }
  finishApply(changed.includes(key) ? changed : [key, ...changed]);
}

// ── Transport ─────────────────────────────────────────────────────────────────────────────

interface DeviceResponse {
  status: number;
  body: any;
  retryAfterMs: number | null;
}

/** A plain fetch with the session's store token. Never throws, never signs anyone out. */
async function deviceFetch(token: string, path: string, init: RequestInit): Promise<DeviceResponse | null> {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;
  try {
    const res = await fetch(`${ApiBaseUrl.apiBaseUrl}/${path}`, {
      ...init,
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      ...(controller ? { signal: controller.signal } : {})
    });
    let body: any = null;
    if (res.status === 200 || res.status === 429) {
      try {
        body = await res.json();
      } catch {
        body = null;
      }
    }
    // Retry-After is not CORS-exposed by default: fall back to a body field, then to 10 s.
    let retryAfterMs: number | null = null;
    if (res.status === 429) {
      const header = Number(res.headers.get('Retry-After'));
      const fromBody = Number(body?.retryAfterSeconds ?? body?.retryAfter);
      const seconds = Number.isFinite(header) && header > 0 ? header : Number.isFinite(fromBody) && fromBody > 0 ? fromBody : 0;
      retryAfterMs = seconds > 0 ? Math.min(seconds, 300) * 1000 : REPORT_RETRY_FALLBACK_MS;
    }
    return { status: res.status, body, retryAfterMs };
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function getWebAppVersion(): Promise<string | null> {
  try {
    const worker = typeof navigator !== 'undefined' ? navigator.serviceWorker?.controller : null;
    const version = await getWorkerVersion(worker);
    return version ? String(version).slice(0, 50) : null;
  } catch {
    return null;
  }
}

// ── Session ───────────────────────────────────────────────────────────────────────────────

interface ActiveSession {
  seq: number;
  token: string;
  companyId: number;
  deviceId: string;
  /** Register answered (200, or 404/405 = server too old): later pulls use GET resolved. */
  registered: boolean;
  /** The server answered authoritatively once: reports are worth sending. */
  serverReady: boolean;
  pullRequested: boolean;
  loop: Promise<void> | null;
}

let sessionSeq = 0;
let active: ActiveSession | null = null;

// Report scheduling state (module-wide: one report at a time per tab).
let reportTimer: ReturnType<typeof setTimeout> | null = null;
let reportInFlight = false;
let reportAgain = false;
let reportNotBefore = 0;

/** Still the same store session (no logout / login / company change since `s` started)? */
function isCurrent(s: ActiveSession): boolean {
  if (active !== s || s.seq !== sessionSeq) return false;
  const now = getStoreDeviceSession();
  return !!now && now.companyId === s.companyId && now.token === s.token;
}

interface AuthoritativePayload {
  unchanged: boolean;
  revision: string | null;
  values: Record<string, unknown>;
}

function authoritativePayload(s: ActiveSession, res: DeviceResponse | null): AuthoritativePayload | null {
  if (!res || res.status !== 200 || !isCurrent(s)) return null;
  const b = res.body;
  if (!isPlainObject(b) || b.managed !== true) return null;
  if (Number(b.companyId) !== s.companyId) return null;
  if (typeof b.deviceId === 'string' && b.deviceId !== s.deviceId) return null;
  const revision = typeof b.revision === 'string' ? b.revision : null;
  if (b.unchanged === true) return { unchanged: true, revision, values: {} };
  if (!isPlainObject(b.values)) return null;
  return { unchanged: false, revision, values: b.values };
}

/** Runs inside the lock with the ledger re-read there. Returns the keys whose local value changed. */
function applyPayload(s: ActiveSession, payload: AuthoritativePayload): string[] {
  const stored = readLedger();
  const changed: string[] = [];
  if (stored && stored.companyId !== s.companyId) changed.push(...releaseLedger(stored));
  const base: DeviceLedger =
    stored && stored.companyId === s.companyId
      ? stored
      : { companyId: s.companyId, deviceId: s.deviceId, revision: null, managed: {}, prior: {} };

  if (payload.unchanged) {
    // Our revision is current: nothing to (un)manage, just make sure the values are still there.
    return changed.concat(verifyPass(base));
  }

  const incoming: Record<string, LedgerEntry> = {};
  Object.entries(payload.values).forEach(([key, entry]) => {
    const def = KEYS_BY_ID[key];
    if (!def || def.reportOnly) return; // unknown to this build, or report-only
    const value = validManagedValue(def, isPlainObject(entry) ? entry.value : undefined);
    if (value === undefined) {
      // Invalid for this build: skip it — keep whatever we enforced before (no change).
      if (base.managed[key]) incoming[key] = base.managed[key];
      return;
    }
    incoming[key] = { value, level: normalizeLevel(isPlainObject(entry) ? entry.level : undefined) };
  });

  const next: DeviceLedger = {
    companyId: s.companyId,
    deviceId: s.deviceId,
    revision: base.revision,
    managed: { ...base.managed },
    prior: { ...base.prior }
  };

  // 1. The user's own value of every NEWLY managed key — recorded BEFORE anything is written (the
  //    ledger is saved first). A key this ledger already manages has its local value written by
  //    this applier, so it is never recorded as the user's (no prior = keep on release).
  Object.keys(incoming).forEach((key) => {
    if (key in next.prior || base.managed[key]) return;
    const current = readRaw(KEYS_BY_ID[key].storageKey);
    next.prior[key] = { present: current !== null, raw: current };
  });
  const released = Object.keys(base.managed).filter((key) => !(key in incoming));
  Object.keys(incoming).forEach((key) => {
    next.managed[key] = incoming[key];
  });
  if (!writeLedger(next)) return changed; // the priors could not be saved: change nothing

  // 2. Write the managed values.
  Object.entries(incoming).forEach(([key, entry]) => {
    const def = KEYS_BY_ID[key];
    const want = encode(def, entry.value);
    if (readRaw(def.storageKey) !== want && writeRaw(def.storageKey, want)) changed.push(key);
  });

  // 3. Keys no longer managed: the user's own value comes back.
  released.forEach((key) => {
    const def = KEYS_BY_ID[key];
    if (def && restorePrior(def, next.prior[key])) changed.push(key);
    delete next.managed[key];
  });
  Object.keys(next.prior).forEach((key) => {
    if (!(key in next.managed)) delete next.prior[key];
  });
  next.revision = payload.revision;
  // A failed write here only means the released keys are restored again on the next pull.
  writeLedger(next);
  return changed;
}

/** Verify (and settle the company) when an answer is not authoritative. Inside the lock. */
function verifyOnly(s: ActiveSession): string[] {
  const ledger = readLedger();
  if (!ledger) return [];
  if (ledger.companyId !== s.companyId) return releaseLedger(ledger);
  return verifyPass(ledger);
}

async function handleAnswer(s: ActiveSession, res: DeviceResponse | null): Promise<boolean> {
  const payload = authoritativePayload(s, res);
  let changed: string[] = [];
  try {
    changed = await withDeviceSettingsLock(() => {
      if (!isCurrent(s)) return [];
      return payload ? applyPayload(s, payload) : verifyOnly(s);
    });
  } catch {
    changed = [];
  }
  if (!isCurrent(s)) return false;
  finishApply(changed);
  if (payload) {
    s.serverReady = true;
    if (changed.length > 0) scheduleDeviceReport();
  }
  return !!payload;
}

async function register(s: ActiveSession): Promise<void> {
  const appVersion = await getWebAppVersion();
  if (!isCurrent(s)) return;
  const body = {
    deviceId: s.deviceId,
    appType: WEB_APP_TYPE,
    deviceName: getWebDeviceName(),
    platform: describeWebPlatform(),
    appVersion,
    capabilities: { keys: WEB_DEVICE_KEYS.map((d) => d.key) }
  };
  const res = await deviceFetch(s.token, 'DeviceSettings/register', { method: 'POST', body: JSON.stringify(body) });
  if (!isCurrent(s)) return;
  // 404/405: the server predates device settings — no point registering again this session.
  if (res && (res.status === 200 || res.status === 404 || res.status === 405)) s.registered = true;
  const authoritative = await handleAnswer(s, res);
  if (authoritative) scheduleDeviceReport();
}

async function pullResolved(s: ActiveSession): Promise<void> {
  const ledger = readLedger();
  const revision =
    ledger && ledger.companyId === s.companyId && ledger.deviceId === s.deviceId ? ledger.revision : null;
  const qs = new URLSearchParams({ deviceId: s.deviceId, appType: WEB_APP_TYPE });
  if (revision) qs.set('revision', revision);
  const res = await deviceFetch(s.token, `DeviceSettings/resolved?${qs.toString()}`, { method: 'GET' });
  if (!isCurrent(s)) return;
  await handleAnswer(s, res);
}

/** Serialized: at most one register / resolved call in flight; requests made meanwhile coalesce. */
function requestPull(s: ActiveSession): Promise<void> {
  s.pullRequested = true;
  if (!s.loop) {
    s.loop = (async () => {
      while (s.pullRequested && isCurrent(s)) {
        s.pullRequested = false;
        try {
          await (s.registered ? pullResolved(s) : register(s));
        } catch {
          /* never surfaces */
        }
      }
    })().finally(() => {
      s.loop = null;
      if (s.pullRequested && isCurrent(s)) void requestPull(s);
    });
  }
  return s.loop;
}

/**
 * Start (or restart) the device session for the current auth state. Called by NotificationProvider
 * whenever its token changes. A no-op unless this tab is a store session (deviceIdentity).
 */
export function startDeviceSettingsSession(): void {
  stopDeviceSettingsSession();
  const session = getStoreDeviceSession();
  if (!session) return;
  const deviceId = getPersistedWebDeviceId();
  if (!deviceId) return; // no persistent storage: never register an unstable id
  const s: ActiveSession = {
    seq: sessionSeq,
    token: session.token,
    companyId: session.companyId,
    deviceId,
    registered: false,
    serverReady: false,
    pullRequested: false,
    loop: null
  };
  active = s;
  void (async () => {
    // Before any network call (works offline): another company's ledger gives its priors back,
    // and this company's managed values are re-checked.
    let changed: string[] = [];
    try {
      changed = await withDeviceSettingsLock(() => (isCurrent(s) ? verifyOnly(s) : []));
    } catch {
      changed = [];
    }
    if (!isCurrent(s)) return;
    finishApply(changed);
    void requestPull(s);
  })();
}

/** Sign-out / token change: stop timers and in-flight work. The ledger stays. */
export function stopDeviceSettingsSession(): void {
  sessionSeq += 1;
  const wasActive = active !== null;
  active = null;
  if (reportTimer) clearTimeout(reportTimer);
  reportTimer = null;
  reportAgain = false;
  if (wasActive) dispatchApplied([]); // the locks belong to the session
}

/** Pull the resolved values now (SettingsChanged push / reconnect). Never throws. */
export function pullDeviceSettings(): Promise<void> {
  const s = active;
  if (!s || !isCurrent(s)) return Promise.resolve();
  return requestPull(s).catch(() => undefined);
}

/** Join `company_{id}_device_{deviceId}` on the app-wide hub connection (after every (re)connect). */
export async function joinDeviceSettingsGroup(connection: {
  invoke: (methodName: string, ...args: any[]) => Promise<any>;
}): Promise<void> {
  const session = getStoreDeviceSession();
  if (!session) return;
  const deviceId = getPersistedWebDeviceId();
  if (!deviceId) return;
  try {
    await connection.invoke('JoinGroup', `company_${session.companyId}_device_${deviceId}`);
  } catch {
    /* older hub or refused: company and branch pushes still arrive */
  }
}

// ── Report ────────────────────────────────────────────────────────────────────────────────

const utf8Bytes = (text: string): number => {
  try {
    return new TextEncoder().encode(text).length;
  } catch {
    return text.length * 3;
  }
};

/** cyrb53 — a fast, well-spread 53-bit string hash (dedupe only, not security). */
function hashString(text: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

/** Every catalogued key (managed and report-only) with the typed value this browser runs with. */
function buildReportSnapshot(): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  WEB_DEVICE_KEYS.forEach((def) => {
    const value = def.effective(readRaw(def.storageKey));
    const bytes = utf8Bytes(JSON.stringify(value));
    values[def.key] = bytes > MAX_REPORT_VALUE_BYTES ? { $omitted: true, bytes } : value;
  });
  // Keep the body under 1 MB: drop the largest values first (never the case for today's keys).
  let total = utf8Bytes(JSON.stringify(values));
  while (total > MAX_REPORT_BODY_BYTES) {
    const largest = Object.keys(values).sort(
      (a, b) => utf8Bytes(JSON.stringify(values[b])) - utf8Bytes(JSON.stringify(values[a]))
    )[0];
    if (!largest) break;
    values[largest] = { $omitted: true, bytes: utf8Bytes(JSON.stringify(values[largest])) };
    total = utf8Bytes(JSON.stringify(values));
  }
  return values;
}

const canonicalJson = (values: Record<string, unknown>): string =>
  JSON.stringify(
    Object.keys(values)
      .sort()
      .map((k) => [k, values[k]])
  );

function alreadyReported(marker: string): boolean {
  try {
    const p = JSON.parse(localStorage.getItem(REPORT_HASH_KEY) || 'null');
    return isPlainObject(p) && p.marker === marker && Date.now() - Number(p.at) < REPORT_HASH_MAX_AGE_MS;
  } catch {
    return false;
  }
}

async function sendReport(s: ActiveSession): Promise<void> {
  if (!isCurrent(s) || !s.serverReady) return;
  const values = buildReportSnapshot();
  const marker = `${s.companyId}|${s.deviceId}|${hashString(canonicalJson(values))}`;
  if (alreadyReported(marker)) return; // this exact snapshot already reached the server (any tab)
  reportInFlight = true;
  let retryMs: number | null = null;
  try {
    const res = await deviceFetch(s.token, 'DeviceSettings/report', {
      method: 'POST',
      body: JSON.stringify({ deviceId: s.deviceId, appType: WEB_APP_TYPE, values })
    });
    if (!isCurrent(s) || !res) return;
    if (res.status === 200 && !(isPlainObject(res.body) && res.body.managed === false)) {
      writeRaw(REPORT_HASH_KEY, JSON.stringify({ marker, at: Date.now() }));
    } else if (res.status === 429) {
      retryMs = res.retryAfterMs ?? REPORT_RETRY_FALLBACK_MS;
      reportNotBefore = Date.now() + retryMs;
    }
  } finally {
    reportInFlight = false;
    if (retryMs !== null || reportAgain) {
      reportAgain = false;
      scheduleDeviceReport(retryMs ?? REPORT_DEBOUNCE_MS);
    }
  }
}

/** Debounced report of the local values (10 s). The snapshot is taken when it is sent. */
export function scheduleDeviceReport(delayMs: number = REPORT_DEBOUNCE_MS): void {
  const s = active;
  if (!s || !isCurrent(s)) return;
  if (reportInFlight) {
    reportAgain = true;
    return;
  }
  if (reportTimer) clearTimeout(reportTimer);
  const wait = Math.max(delayMs, reportNotBefore - Date.now(), 0);
  reportTimer = setTimeout(() => {
    reportTimer = null;
    void sendReport(s);
  }, wait);
}

/** A screen saved one of the catalogued keys locally: report it (debounced). */
export function noteDeviceSettingSaved(): void {
  scheduleDeviceReport();
}

// ── Locks ─────────────────────────────────────────────────────────────────────────────────

export interface ManagedDeviceSetting {
  locked: boolean;
  level: DeviceSettingLevel | null;
}

const NOT_MANAGED: ManagedDeviceSetting = { locked: false, level: null };

/** Locked only while the ledger belongs to the CURRENT store session's company. */
export function getManagedDeviceSetting(key: string): ManagedDeviceSetting {
  const def = KEYS_BY_ID[key];
  if (!def || def.reportOnly) return NOT_MANAGED;
  const session = getStoreDeviceSession();
  if (!session) return NOT_MANAGED;
  const ledger = readLedger();
  if (!ledger || ledger.companyId !== session.companyId) return NOT_MANAGED;
  const entry = ledger.managed[key];
  return entry ? { locked: true, level: entry.level } : NOT_MANAGED;
}

/** `{ locked, level }` for a control bound to `key`; re-renders when the ledger or session changes. */
export function useManagedDeviceSetting(key: string): ManagedDeviceSetting {
  const [state, setState] = useState<ManagedDeviceSetting>(() => getManagedDeviceSetting(key));
  useEffect(() => {
    const refresh = () => {
      const next = getManagedDeviceSetting(key);
      setState((prev) => (prev.locked === next.locked && prev.level === next.level ? prev : next));
    };
    refresh();
    window.addEventListener(DEVICE_SETTINGS_APPLIED_EVENT, refresh);
    window.addEventListener(AUTH_TOKEN_CHANGED_EVENT, refresh);
    return () => {
      window.removeEventListener(DEVICE_SETTINGS_APPLIED_EVENT, refresh);
      window.removeEventListener(AUTH_TOKEN_CHANGED_EVENT, refresh);
    };
  }, [key]);
  return state;
}

/** Calls `handler` when an apply changed one of `keys` (or everything): re-read it from storage. */
export function useDeviceSettingsApplied(keys: readonly string[], handler: () => void): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const keyList = keys.join('|');
  useEffect(() => {
    const wanted = keyList.split('|');
    const listener = (e: Event) => {
      const changed = (e as CustomEvent<DeviceSettingsAppliedDetail>).detail?.keys ?? [];
      if (changed.includes('*') || changed.some((k) => wanted.includes(k))) handlerRef.current();
    };
    window.addEventListener(DEVICE_SETTINGS_APPLIED_EVENT, listener);
    return () => window.removeEventListener(DEVICE_SETTINGS_APPLIED_EVENT, listener);
  }, [keyList]);
}
